import produce from 'immer';
import {
  AmDurationType,
  AmLocation,
  AmRooms,
  LatLong,
  Location,
  Locations,
  LocationType,
  PaxAmount,
  PreviousSearch,
  TravelClass,
  TravelDates,
  TravelType,
  WidgetTab,
  SearchType,
} from '../common';
import { GtmEvent, GtmPassengers, GtmService } from '../config';
import { asCmsLang, DeepPartial, isBlank, isClient, isPresent, mergeArray, mergeDeep } from '../utils';
import { BookingState, CompactWidgetState } from './state';
import { amDayIsSearchable, durationIsValid, locationsAreSame, originCode, validateAmFields } from './stateUtils';
import { ActionsFactory, StoreActions } from './types';

export interface Actions {
  setSelection: (previousSearch: PreviousSearch) => BookingState;
  setTravelType: (travelType: TravelType) => BookingState;
  setPaxAmount: (paxAmount: PaxAmount) => BookingState;
  setTravelClass: (travelClass: TravelClass) => BookingState;
  setLocations: (locations: Locations) => BookingState;
  setAmRooms: (rooms: AmRooms) => BookingState;
  setAmOrigin: (origin: Location) => BookingState;
  setAmDestination: (destination: AmLocation) => BookingState;
  setAmDuration: (duration: string) => BookingState;
  setTravelDates: (travelDates?: TravelDates, isFlexibleCalendar?: boolean) => BookingState;
  setDirectFlights: (directFlights: boolean) => BookingState;
  setLocationQuery: (locationQuery: string) => BookingState;
  setPreviousSearchesEnabled: (enablePreviousSearches: boolean) => BookingState;
  proceedToBooking: () => BookingState;
  proceedToAurinkomatkat: () => BookingState;
  setUserLocation: (userLocation: LatLong) => BookingState;
  disableGeoLocation: () => BookingState;
  setLocatingUser: (locating: boolean) => BookingState;
  returnFromAirCalendar: () => BookingState;
  setActiveLocationIndex: (activeLocationIndex: number) => BookingState;
  setCurrentTab: (tab: WidgetTab) => BookingState;
  logGtmUiEvent: (eventData: { id: string; state: string }) => BookingState;
  mergePartialState: (updateState: DeepPartial<BookingState>) => BookingState;
  setPromoCode: (promoCode: string) => BookingState;
  setCompactWidgetState: (direction: CompactWidgetState) => BookingState;
  setPromoCodeNotFoundError: (error: boolean) => BookingState;
  setLocationType: (locationType: LocationType) => BookingState;
}

const gtmEvent = (
  service: GtmService,
  id: string,
  state: string,
  locale: string,
  currentTab: WidgetTab,
  pagePathParameter?: string
): void =>
  service.log(GtmEvent.UI_EVENT, {
    context: currentTab === WidgetTab.AM ? 'flight-search-aurinkomatkat' : 'flight-search',
    triggerElement: 'BOOKING_WIDGET',
    id,
    state,
    language: asCmsLang(locale),
    // pagePathParameter indicates some virtual pages e.g. price-matrix
    pagePath: isClient()
      ? `${window.location.href}${
          pagePathParameter ? (window.location.href.endsWith('/') ? pagePathParameter : `/${pagePathParameter}`) : ''
        }`
      : '',
    searchType:
      currentTab === WidgetTab.AM
        ? undefined
        : currentTab === WidgetTab.FLIGHT
        ? SearchType.BOOKING_FLOW
        : SearchType.AWARD,
  });

const asGtmPassengers = (paxAmount: PaxAmount): GtmPassengers => {
  const { adults, c15s, children, infants } = paxAmount;
  return {
    adults,
    children: children + c15s,
    infants,
  };
};

const asGtmAmPassengers = (rooms: AmRooms): string => {
  const { adults, children, childrenAges } = rooms.reduce(
    (totals, { adults, children, childrenAges }) => ({
      adults: totals.adults + adults,
      children: totals.children + children,
      childrenAges: [...totals.childrenAges, ...childrenAges],
    }),
    {
      adults: 0,
      children: 0,
      childrenAges: [],
    }
  );

  return `ROOMS: ${rooms.length}, ADT: ${adults}, CHD: ${children}, AGES: ${childrenAges.join('-')}`;
};

const storeActions: StoreActions<BookingState, Actions> = {
  setSelection: (state: BookingState, searchState: PreviousSearch): BookingState =>
    produce(state, (state: BookingState) => {
      const { selection, ux } = state;
      selection.travelType = searchState.travelType;
      selection.travelDates = searchState.travelDates;
      selection.travelClass = searchState.travelClass;
      selection.paxAmount = searchState.paxAmount;
      selection.directFlights = searchState.directFlights;
      selection.locations = [{}];
      selection.locations[0].origin = {
        ...selection.locations[0].origin,
        locationCode: searchState.origin.locationCode,
      };
      selection.locations[0].destination = {
        ...selection.locations[0].destination,
        locationCode: searchState.destination.locationCode,
      };
      selection.promoCode = searchState.promoCode || null;
      state.forceUpdate = true;
      ux.compactWidgetState = CompactWidgetState.OPENED;
    }),
  setTravelType: (state: BookingState, travelType: TravelType): BookingState =>
    produce(state, ({ selection, ux, extensionApi: { gtmService }, shouldExpandWidget, currentTab }: BookingState) => {
      gtmEvent(gtmService, 'travel-type', travelType.toUpperCase(), selection.locale, currentTab);
      selection.travelType = travelType;
      if (travelType === TravelType.ONE_WAY && isPresent(selection.travelDates)) {
        delete selection.travelDates.returnDate;
      }

      if (travelType === TravelType.MULTI_CITY) {
        ux.compactWidgetState = CompactWidgetState.OPENED;
        selection.locations[1] = {};
      } else if (!selection.locations[0].destination && !shouldExpandWidget) {
        ux.compactWidgetState = CompactWidgetState.CLOSED;
      }

      if (travelType !== TravelType.MULTI_CITY && selection.locations.length > 1) {
        selection.locations = [selection.locations[0]];
        selection.activeLocationIndex = 0;
      }
    }),
  setPaxAmount: (state: BookingState, paxAmount: PaxAmount): BookingState =>
    produce(state, ({ selection, extensionApi: { gtmService }, currentTab }: BookingState) => {
      const { adults, children, infants } = asGtmPassengers(paxAmount);
      gtmEvent(
        gtmService,
        'pax-amount',
        `MAINPAX: ${adults}, CHD: ${children}, INF: ${infants}, MAINPAXTYPE: ADT`,
        selection.locale,
        currentTab
      );
      selection.paxAmount = paxAmount;
    }),
  setTravelClass: (state: BookingState, travelClass: TravelClass): BookingState =>
    produce(
      state,
      ({ currentTab, selection, amState: { amSelection }, extensionApi: { gtmService } }: BookingState) => {
        gtmEvent(gtmService, 'travel-class', travelClass.toUpperCase(), selection.locale, currentTab);
        if (currentTab === WidgetTab.AM) {
          amSelection.travelClass = travelClass;
        } else {
          selection.travelClass = travelClass;
        }
      }
    ),
  setCompactWidgetState: (state: BookingState, compactWidgetState: CompactWidgetState): BookingState =>
    produce(state, ({ ux }) => {
      ux.compactWidgetState = compactWidgetState;
    }),
  setLocationType: (state: BookingState, locationType: LocationType): BookingState =>
    produce(state, ({ ux }) => {
      ux.locationType = locationType;
    }),
  setLocations: (state: BookingState, locations: Locations): BookingState =>
    produce(state, ({ selection, location, extensionApi: { gtmService }, ux, currentTab }: BookingState) => {
      gtmEvent(
        gtmService,
        'locations',
        `${locations?.origin?.locationCode} - ${locations?.destination?.locationCode}`,
        selection.locale,
        currentTab
      );
      location.query = '';
      delete location.results;
      if (!locationsAreSame(locations)) {
        if (locations.origin && locations.destination) {
          ux.compactWidgetState = CompactWidgetState.OPENED;
        }
        selection.locations[selection.activeLocationIndex] = locations;
        return;
      }
      if (originCode(selection.locations[selection.activeLocationIndex]) !== originCode(locations)) {
        selection.locations[selection.activeLocationIndex].origin = locations.origin;
        delete selection.locations[selection.activeLocationIndex].destination;
      } else {
        delete selection.locations[selection.activeLocationIndex].origin;
        selection.locations[selection.activeLocationIndex].destination = locations.destination;
      }
    }),
  setAmRooms: (state: BookingState, amRooms: AmRooms): BookingState =>
    produce(
      state,
      ({ amState: { amSelection }, selection: { locale }, extensionApi: { gtmService } }: BookingState) => {
        const roomBreakDown = amRooms.map(
          ({ adults, children, childrenAges }, i) =>
            `ROOM: ${i + 1}, ADT: ${adults}, CHD: ${children}, AGES: ${childrenAges.join('-')}`
        );
        gtmEvent(gtmService, 'paxAmount', roomBreakDown.join(';'), locale, WidgetTab.AM);

        amSelection.rooms = amRooms;
        amSelection.amProceedEnabled = validateAmFields(amSelection);
      }
    ),
  setAmOrigin: (state: BookingState, origin: Location) =>
    produce(
      state,
      ({ amState: { amSelection }, selection: { locale }, extensionApi: { gtmService }, ux }: BookingState) => {
        gtmEvent(gtmService, 'origin', origin.locationCode, locale, WidgetTab.AM);

        if (amSelection.locations.destination) {
          ux.compactWidgetState = CompactWidgetState.OPENED;
        }

        amSelection.locations.origin = origin;
        amSelection.amProceedEnabled = validateAmFields(amSelection);
      }
    ),
  setAmDestination: (state: BookingState, destination: AmLocation) =>
    produce(
      state,
      ({
        amState: { amSelection, holidayTypes },
        selection: { locale },
        extensionApi: { gtmService },
        ux,
      }: BookingState) => {
        const originCode = amSelection.locations.origin?.locationCode;
        const destinationCode = destination?.destinationCode;
        const holidayType = holidayTypes[destination.holidayType];

        gtmEvent(
          gtmService,
          'destination',
          `${originCode} - ${destinationCode} - ${holidayType?.label}`,
          locale,
          WidgetTab.AM
        );

        amSelection.locations.destination = destination;
        amSelection.locations.holidayType = holidayType;
        amSelection.travelType = holidayType.type === AmDurationType.LIST ? TravelType.ONE_WAY : TravelType.ROUND_TRIP;
        // Clear the duration under some conditions (i.e. duration not set or selected duration is invalid)
        if (!durationIsValid(amSelection.duration, amSelection.locations.holidayType)) {
          delete amSelection.duration;
        }
        // Clear the departure date if the destination has changed so that the date is no longer within the
        // searchable limits for the destination
        if (!amDayIsSearchable(amSelection.amTravelDates.departureDate, amSelection.locations.destination)) {
          delete amSelection.amTravelDates.departureDate;
          delete amSelection.amTravelDates.returnDate;
        }
        // Reset departure date to return date if holiday type changes
        if (
          state.amState.amSelection.locations.holidayType?.type === AmDurationType.DAYS &&
          holidayType?.type === AmDurationType.LIST
        ) {
          delete amSelection.amTravelDates.returnDate;
        }

        amSelection.amProceedEnabled = validateAmFields(amSelection);

        if (amSelection.locations.origin) {
          ux.compactWidgetState = CompactWidgetState.OPENED;
        }
      }
    ),
  setAmDuration: (state: BookingState, duration: string) =>
    produce(
      state,
      ({ amState: { amSelection }, extensionApi: { gtmService }, selection: { locale } }: BookingState) => {
        gtmEvent(gtmService, 'duration', duration, locale, WidgetTab.AM);

        amSelection.duration = duration;
        amSelection.amProceedEnabled = validateAmFields(amSelection);
      }
    ),
  setTravelDates: (
    state: BookingState,
    { departureDate, returnDate, flexible }: TravelDates = {},
    isFlexibleCalendar = false
  ): BookingState =>
    produce(
      state,
      ({
        selection: { travelDates, locale },
        currentTab,
        amState: { amSelection },
        extensionApi: { gtmService },
      }: BookingState) => {
        gtmEvent(
          gtmService,
          isFlexibleCalendar ? 'price-matrix' : 'travel-dates',
          `DEPARTURE: ${departureDate}, RETURN: ${returnDate}`,
          locale,
          currentTab,
          isFlexibleCalendar ? 'price-matrix' : null
        );

        if (currentTab === WidgetTab.AM) {
          amSelection.amTravelDates.departureDate = departureDate;
          amSelection.amTravelDates.returnDate = returnDate;
          amSelection.amTravelDates.flexible = flexible;
          amSelection.amProceedEnabled = validateAmFields(amSelection);
        } else {
          travelDates.departureDate = departureDate;
          travelDates.returnDate = returnDate;
          travelDates.flexible = flexible;
        }
      }
    ),
  setDirectFlights: (state: BookingState, directFlights: boolean): BookingState =>
    produce(state, ({ selection, amState, currentTab, extensionApi: { gtmService } }) => {
      gtmEvent(gtmService, 'toggle-direct-flights', String(directFlights), selection.locale, currentTab);
      if (currentTab === WidgetTab.AM) {
        amState.amSelection.directFlights = directFlights;
      } else {
        selection.directFlights = directFlights;
      }
    }),
  setLocationQuery: (state: BookingState, locationQuery: string): BookingState =>
    produce(state, ({ location }) => {
      location.query = locationQuery;
      if (isBlank(locationQuery)) {
        delete location.results;
      }
    }),
  proceedToBooking: (state: BookingState): BookingState =>
    produce(state, ({ selection, ux, extensionApi: { recommendationService } }) => {
      if (
        selection?.locations[selection.activeLocationIndex]?.origin &&
        selection?.locations[selection.activeLocationIndex]?.destination
      ) {
        const origin = selection.locations[selection.activeLocationIndex].origin;
        const destination = selection.locations[selection.activeLocationIndex].destination;
        recommendationService.sendFlightSearchEvent(
          selection?.travelDates?.departureDate,
          selection?.travelDates?.returnDate,
          origin.type === 'airport' ? origin.locationCode : undefined,
          destination.type === 'airport' ? destination.locationCode : undefined,
          origin.type === 'city' ? origin.locationCode : origin.locationCityCode,
          destination.type === 'city' ? destination.locationCode : destination.locationCityCode,
          selection.paxAmount.adults + selection.paxAmount.c15s,
          selection.paxAmount.children + selection.paxAmount.infants
        );
      }
      ux.proceedToBooking = true;
    }),
  setPreviousSearchesEnabled: (state: BookingState, enablePreviousSearches: boolean): BookingState =>
    produce(state, ({ ux }) => {
      ux.previousSearchesEnabled = enablePreviousSearches;
    }),
  proceedToAurinkomatkat: (state: BookingState): BookingState =>
    produce(
      state,
      ({ amState: { amSelection, holidayTypes }, extensionApi: { gtmService }, selection: { locale } }) => {
        const amLocations = `LOCATIONS: ${amSelection.locations.origin?.locationCode}-${amSelection.locations.destination?.destinationCode}`;
        const amTravelDates = `DEPARTURE: ${amSelection.amTravelDates.departureDate};RETURN: ${amSelection.amTravelDates.returnDate}`;
        const amDuration = `DURATION: ${amSelection.duration}`;
        const amHolidayType = `HOLIDAY_TYPE: ${holidayTypes[amSelection.locations.destination?.holidayType]?.label}`;
        const amPassengers = asGtmAmPassengers(amSelection.rooms);
        const amSearchString = `${amLocations};${amTravelDates};${amDuration};${amHolidayType};${amPassengers}`;

        gtmEvent(gtmService, 'aurinkomatkat-search', amSearchString, locale, WidgetTab.AM);
      }
    ),
  setUserLocation: (state: BookingState, { lat, long }: LatLong): BookingState =>
    produce(state, ({ ux, selection }) => {
      ux.userLocation = { lat, long };
      selection.locations[0].origin = undefined;
    }),
  disableGeoLocation: (state: BookingState): BookingState =>
    produce(state, ({ ux }) => {
      ux.geoLocationDisabled = true;
      ux.locatingUser = false;
    }),
  returnFromAirCalendar: (state: BookingState): BookingState =>
    produce(state, ({ ux, price }) => {
      price.airCalendar = undefined;
      ux.proceedToBooking = false;
    }),
  setLocatingUser: (state: BookingState, locating: boolean): BookingState =>
    produce(state, ({ ux }) => {
      ux.locatingUser = locating;
    }),
  setActiveLocationIndex: (state: BookingState, activeLocationIndex: number): BookingState =>
    produce(state, ({ selection }) => {
      selection.activeLocationIndex = activeLocationIndex;

      if (selection.travelType === TravelType.MULTI_CITY && !selection.locations[1]) {
        selection.locations[1] = {};
      }
    }),
  setCurrentTab: (state: BookingState, tab: WidgetTab): BookingState =>
    // For some reason deconstructing the draft state object in the function params made it so
    // that currentTab wasn't recognized and tab change would not work.
    produce(state, (draft: BookingState) => {
      draft.selection.activatedTab = tab;
      draft.currentTab = tab;

      if (tab === WidgetTab.AWARD && state.selection.travelType === TravelType.MULTI_CITY) {
        draft.selection.travelType = TravelType.ROUND_TRIP;
        draft.selection.locations = [state.selection.locations[0]];
        draft.selection.activeLocationIndex = 0;
      }
    }),
  logGtmUiEvent: (state: BookingState, eventData: { id: string; state: string }) => {
    const {
      extensionApi,
      currentTab,
      selection: { locale },
    } = state;

    extensionApi.gtmService?.log(GtmEvent.UI_EVENT, {
      context: currentTab === WidgetTab.FLIGHT ? 'flight-search' : 'flight-search-aurinkomatkat',
      triggerElement: 'BOOKING_WIDGET',
      id: eventData.id,
      state: eventData.state,
      language: asCmsLang(locale),
      pagePath: isClient() ? window.location.href : '',
      searchType:
        currentTab === WidgetTab.AM
          ? undefined
          : currentTab === WidgetTab.FLIGHT
          ? SearchType.BOOKING_FLOW
          : SearchType.AWARD,
    });

    return state;
  },
  mergePartialState: (state: BookingState, updateState: DeepPartial<BookingState>): BookingState =>
    mergeDeep([state, updateState], { overwriteWithEmpty: false, arrayMerge: mergeArray }) as BookingState,
  setPromoCode: (state: BookingState, promoCode: string): BookingState =>
    produce(state, ({ selection }) => {
      selection.promoCode = promoCode;
      selection.promoCodeNotFoundError = null;
      if (!promoCode) {
        selection.promoCodeTerms = null;
      }
    }),
  setPromoCodeNotFoundError: (state: BookingState, error: boolean): BookingState =>
    produce(state, ({ selection }) => {
      selection.promoCodeNotFoundError = error;
    }),
};

export const actions: ActionsFactory<BookingState, Actions> = () => storeActions;
